import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';

interface IpAddressFieldProps {
  ipAddressName: string;
  cidrName: string;
  registerOptions: {
    [key: string]: {
      required?: string;
      validate?: (value: any) => string | boolean | Promise<string | boolean>;
    };
  };
}

const IpAddressField = ({ ipAddressName, cidrName, registerOptions }: IpAddressFieldProps) => {
  const { t } = useTranslation('gateway');
  const { control } = useFormContext();

  const inputAdornmentSx = {
    backgroundColor: '#EEEEEE',
    height: '100%',
    maxHeight: '100%',
    position: 'absolute',
    right: 0,
    margin: 0,
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
  };
  const separatorSx = { marginRight: 1 };
  const cidrFieldSx = {
    width: '20px',
    '& .MuiInputBase-input': {
      color: Constants.Colors.onSurfaceVariant,
      textAlign: 'center',
    },
    '& .MuiInput-underline:before': { borderBottom: 'none' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': { borderBottom: 'none' },
  };

  return (
    <Controller
      name={ipAddressName}
      control={control}
      rules={registerOptions[ipAddressName as keyof typeof registerOptions]}
      render={({ field: ipField, fieldState: { error: ipError } }) => {
        const hasError = !!ipError;
        const errorMessage = ipError?.message;

        return (
          <TextField
            {...ipField}
            id={`${ipAddressName}Input`}
            label={t('details.information.ipAddressLabel')}
            variant="outlined"
            fullWidth
            error={hasError}
            helperText={errorMessage?.toString()}
            slotProps={{
              htmlInput: {
                'data-testid': `${ipAddressName}Input`,
                maxLength: 15,
              },
              input: {
                endAdornment: (
                  <InputAdornment position="end" sx={inputAdornmentSx}>
                    <Typography variant="body1" sx={separatorSx}>
                      /
                    </Typography>
                    <Controller
                      name={cidrName}
                      control={control}
                      rules={registerOptions[cidrName as keyof typeof registerOptions]}
                      render={({ field: cidrField }) => (
                        <TextField
                          {...cidrField}
                          variant="standard"
                          error={false}
                          slotProps={{
                            htmlInput: {
                              'data-testid': `${cidrName}Input`,
                              maxLength: 2,
                              readOnly: true,
                              style: { cursor: 'default' },
                            },
                          }}
                          sx={cidrFieldSx}
                        />
                      )}
                    />
                  </InputAdornment>
                ),
              },
            }}
          />
        );
      }}
    />
  );
};

export default IpAddressField;
